<template>
    <div v-if="visible">
        <!-- Collapsed rail: the card has no room, so it becomes its icon. Links to the
             checklist while tasks remain; once finished there is nowhere to go. -->
        <template v-if="collapsed">
            <Link
                v-if="! complete"
                :href="route('onboarding.show')"
                class="w-full text-white hover:text-white hover:bg-gray-800 group flex items-center justify-center rounded-md p-2"
                :title="`Onboarding hub — ${progress}% Completed`"
                data-test="onboarding-hub-card"
            >
                <font-awesome-icon :icon="faListCheck" size="lg" class="shrink-0 text-white" aria-hidden="true" />
                <span class="sr-only">Onboarding hub — {{ progress }}% Completed</span>
            </Link>
            <span
                v-else
                class="w-full text-white flex items-center justify-center rounded-md p-2"
                :title="`Onboarding hub — ${progress}% Completed`"
                data-test="onboarding-hub-card"
            >
                <font-awesome-icon :icon="faListCheck" size="lg" class="shrink-0 text-white" aria-hidden="true" />
                <span class="sr-only">Onboarding hub — {{ progress }}% Completed</span>
            </span>
        </template>

        <component
            :is="complete ? 'div' : Link"
            v-else
            v-bind="complete ? {} : { href: route('onboarding.show') }"
            class="relative block bg-white rounded-xl shadow-sm mb-2 px-4 py-3"
            :class="complete ? '' : 'hover:bg-gray-50 transition-colors'"
            data-test="onboarding-hub-card"
        >
            <span class="block text-sm font-bold text-gray-900" :class="complete ? 'pr-6' : ''">Onboarding hub</span>
            <span
                class="block h-1.5 w-full rounded-full bg-gray-200 mt-2 overflow-hidden"
                role="progressbar"
                aria-label="Onboarding progress"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="progress"
            >
                <span class="block h-full rounded-full bg-green-600" :style="{ width: `${progress}%` }" />
            </span>
            <span class="block text-xs text-gray-500 mt-1.5">{{ progress }}% Completed</span>

            <!-- 86bc2cgxd (Rich, 2026-09-24): only a finished hub can be closed. -->
            <button
                v-if="complete"
                type="button"
                class="absolute right-2 top-2 rounded p-1 text-pink-600 hover:bg-pink-50 hover:text-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
                aria-label="Close onboarding hub"
                data-test="onboarding-hub-dismiss"
                @click="dismiss"
            >
                <font-awesome-icon :icon="faXmark" class="h-4 w-4" aria-hidden="true" />
            </button>
        </component>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import axios from 'axios'
import { Link, usePage } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faListCheck, faXmark } from '@fortawesome/pro-light-svg-icons'

defineProps({
    // The desktop rail collapses to 64px, so the card renders as its icon there.
    collapsed: {
        type: Boolean,
        default: false,
    },
})

const page = usePage()

// Hidden straight away on close; the server stops sharing it from the next page.
const dismissed = ref(false)

const complete = computed(() => page.props.onboarding?.complete === true)

// 86bc2cgxd: while the landlord's onboarding checklist is active, and after it is
// finished until they close the card. The shared prop is null for everyone else.
const visible = computed(() => (
    ! dismissed.value
    && (page.props.onboarding?.active === true || complete.value)
))

const progress = computed(() => page.props.onboarding?.progress ?? 0)

// The mobile drawer and the desktop sidebar each mount a card, so a close in one
// must not leave the other on screen: both read the same page prop.
const dismiss = () => {
    dismissed.value = true

    if (page.props.onboarding) {
        page.props.onboarding = null
    }

    axios.post(window.route('onboarding.hub.dismiss')).catch(() => {})
}
</script>
